import React from 'react';
import {Text, View} from '@react-pdf/renderer';
import type {Style} from '@react-pdf/types';
import {IntlShape} from 'react-intl';
import Util from '../../../../forecast-app/shared/util/util';
import {buildEstimate, formatEstimateHours, ratePerUnit, type EstimateModel, type EstimateRow} from '../estimate';
import QuotePage from './QuotePage';
import SectionLabel from './SectionLabel';
import {buildStyles} from './quotePdfStyles';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';

const EM_DASH = '—';

type QuoteEstimatePageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
	intl: IntlShape;
};

type Formatters = {
	money: (amount: number) => string;
	time: (hours: number) => string;
	rate: (ratePerHour: number) => string;
};

const FIRST_COLUMN_HEADER_KEY: Record<EstimateModel['firstColumn'], string> = {
	roles: 'quote_designer.pdf.column.role',
	phase: 'common.phase',
	deliverable: 'common.deliverable',
	project: 'common.description',
};

const renderRow = (
	row: EstimateRow,
	index: number,
	model: EstimateModel,
	styles: ReturnType<typeof buildStyles>,
	brand: string,
	intl: IntlShape,
	format: Formatters
): JSX.Element | null => {
	const numericCells = (hours: number | null, rate: number | null, total: number | null, totalStyle: Style) => (
		<>
			{model.showTime && (
				<Text style={[styles.itemNum, styles.colNum]}>{hours == null ? EM_DASH : format.time(hours)}</Text>
			)}
			{model.showRate && (
				<Text style={[styles.itemNum, styles.colNum]}>{rate == null ? EM_DASH : format.rate(rate)}</Text>
			)}
			<Text style={[totalStyle, styles.colNum]}>{total == null ? '' : format.money(total)}</Text>
		</>
	);

	switch (row.kind) {
		case 'phaseHeader':
			return (
				<View key={index} style={styles.phaseRow} wrap={false}>
					<View style={[styles.colLabel, styles.phaseCell]}>
						<Text style={[styles.phaseIndex, {color: brand}]}>
							{intl.formatMessage({id: 'quote_designer.pdf.phase_index'}, {index: row.index})}
						</Text>
						<Text style={styles.phaseName}>{row.label}</Text>
					</View>
					{model.showTime && <Text style={styles.colNum} />}
					{model.showRate && <Text style={styles.colNum} />}
					<Text style={[styles.phaseSubtotal, styles.colNum]}>
						{row.subtotal == null ? '' : format.money(row.subtotal)}
					</Text>
				</View>
			);
		case 'deliverableHeader':
			return (
				<View key={index} style={styles.deliverableRow} wrap={false}>
					<Text style={[styles.deliverableLabel, {color: brand}]}>
						{intl.formatMessage({id: 'quote_designer.pdf.deliverable'})}
					</Text>
					<Text style={styles.deliverableName}>{row.label}</Text>
				</View>
			);
		case 'item':
			return (
				<View key={index} style={styles.itemRow} wrap={false}>
					<View style={[styles.colLabel, styles.itemLabelCell]}>
						{row.itemKind === 'expense' && (
							<Text style={styles.expenseTag}>{intl.formatMessage({id: 'quote_designer.pdf.expense'})}</Text>
						)}
						<Text style={styles.itemName}>{row.label}</Text>
					</View>
					{numericCells(row.hours, row.rate, row.total, styles.itemTotal)}
				</View>
			);
		case 'expensesTotal':
			return (
				<View key={index} style={styles.expensesTotalRow} wrap={false}>
					<Text style={[styles.expensesTotalLabel, styles.colLabel]}>
						{intl.formatMessage({id: 'quote_designer.pdf.expenses_total'})}
					</Text>
					{model.showTime && <Text style={styles.colNum} />}
					{model.showRate && <Text style={styles.colNum} />}
					<Text style={[styles.itemTotal, styles.colNum]}>{format.money(row.total)}</Text>
				</View>
			);
		case 'rollup':
			return (
				<View key={index} style={styles.rollupRow} wrap={false}>
					<Text style={[styles.rollupName, styles.colLabel]}>{row.label}</Text>
					{numericCells(row.hours, null, row.total, styles.itemTotal)}
				</View>
			);
		default:
			return null;
	}
};

/** The estimate table plus its totals block, computed from the frozen line items. */
const QuoteEstimatePage = ({data, template, intl}: QuoteEstimatePageProps): JSX.Element => {
	const styles = buildStyles(template.font);
	const brand = template.brandColor;
	const model = buildEstimate(data, template);
	const currencySymbol = Util.GetCurrencySymbol(data.currency);
	const format: Formatters = {
		money: amount => Util.getFormattedNumberWithCurrency(currencySymbol, amount, intl),
		time: hours => formatEstimateHours(hours, model.unit, model.minutesPerDay),
		rate: rate =>
			Util.getFormattedNumberWithCurrency(currencySymbol, ratePerUnit(rate, model.unit, model.minutesPerDay), intl),
	};
	const days = model.unit === 'days';
	const {totals} = model;

	return (
		<QuotePage data={data} template={template} intl={intl}>
			<View style={styles.section}>
				<SectionLabel font={template.font} brandColor={brand}>
					{intl.formatMessage({id: 'quote_designer.section.estimate'})}
				</SectionLabel>

				<View style={styles.tableHeadRow}>
					<Text style={[styles.th, styles.colLabel]}>
						{intl.formatMessage({id: FIRST_COLUMN_HEADER_KEY[model.firstColumn]})}
					</Text>
					{model.showTime && (
						<Text style={[styles.th, styles.thNum, styles.colNum]}>
							{intl.formatMessage({id: days ? 'common.days' : 'common.hours'})}
						</Text>
					)}
					{model.showRate && (
						<Text style={[styles.th, styles.thNum, styles.colNum]}>
							{intl.formatMessage({
								id: days ? 'quote_designer.pdf.column.rate_day' : 'quote_designer.pdf.column.rate_hour',
							})}
						</Text>
					)}
					<Text style={[styles.th, styles.thNum, styles.colNum]}>{intl.formatMessage({id: 'common.total'})}</Text>
				</View>

				{model.rows.map((row, index) => renderRow(row, index, model, styles, brand, intl, format))}

				<View style={styles.totalsWrap}>
					<View style={styles.totalsGrid}>
						{template.showSubtotal && (
							<View style={styles.totalsRow}>
								<Text style={styles.totalsLabel}>{intl.formatMessage({id: 'common.subtotal'})}</Text>
								<Text style={styles.totalsValue}>
									{format.money(totals.expensesFolded ? totals.subtotal + totals.expenses : totals.subtotal)}
								</Text>
							</View>
						)}
						{template.showExpensesRow && !totals.expensesFolded && (
							<View style={styles.totalsRow}>
								<Text style={styles.totalsLabel}>
									{intl.formatMessage({id: 'quote_designer.pdf.totals.expenses'})}
								</Text>
								<Text style={styles.totalsValue}>{format.money(totals.expenses)}</Text>
							</View>
						)}
						{template.showGrandTotal && (
							<View style={styles.totalsGrandRow}>
								<Text style={styles.totalsGrandLabel}>{intl.formatMessage({id: 'common.total'})}</Text>
								<Text style={[styles.totalsGrandValue, {color: brand}]}>{format.money(totals.grandTotal)}</Text>
							</View>
						)}
					</View>
				</View>
			</View>
		</QuotePage>
	);
};

export default QuoteEstimatePage;
